// Minimal column-major 3D math. Matrices are Float32Array(16) laid out the way
// WGSL expects them, so they can be written straight into a uniform buffer.

export type Vec3 = [number, number, number];
export type Mat4 = Float32Array<ArrayBuffer>;

export const vec3 = (x = 0, y = 0, z = 0): Vec3 => [x, y, z];

export const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const scale = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];
export const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

export const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

export function normalize(a: Vec3): Vec3 {
  const l = Math.hypot(a[0], a[1], a[2]);
  return l > 1e-8 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 0, 0];
}

export const mat4 = (): Mat4 => new Float32Array(16);

export function identity(out: Mat4 = mat4()): Mat4 {
  out.fill(0);
  out[0] = out[5] = out[10] = out[15] = 1;
  return out;
}

/** Reverse-Z infinite perspective: near plane maps to 1, infinity to 0. */
export function perspectiveReverseZ(fovY: number, aspect: number, near: number, out: Mat4 = mat4()): Mat4 {
  const f = 1 / Math.tan(fovY / 2);
  out.fill(0);
  out[0] = f / aspect;
  out[5] = f;
  out[11] = -1;
  out[14] = near;
  return out;
}

export function lookAt(eye: Vec3, target: Vec3, up: Vec3, out: Mat4 = mat4()): Mat4 {
  const z = normalize(sub(eye, target));
  const x = normalize(cross(up, z));
  const y = cross(z, x);
  out[0] = x[0]; out[1] = y[0]; out[2] = z[0]; out[3] = 0;
  out[4] = x[1]; out[5] = y[1]; out[6] = z[1]; out[7] = 0;
  out[8] = x[2]; out[9] = y[2]; out[10] = z[2]; out[11] = 0;
  out[12] = -dot(x, eye); out[13] = -dot(y, eye); out[14] = -dot(z, eye); out[15] = 1;
  return out;
}

export function multiply(a: Mat4, b: Mat4, out: Mat4 = mat4()): Mat4 {
  for (let c = 0; c < 4; c++) {
    const b0 = b[c * 4]!, b1 = b[c * 4 + 1]!, b2 = b[c * 4 + 2]!, b3 = b[c * 4 + 3]!;
    for (let r = 0; r < 4; r++) {
      out[c * 4 + r] = a[r]! * b0 + a[4 + r]! * b1 + a[8 + r]! * b2 + a[12 + r]! * b3;
    }
  }
  return out;
}

/** Compose translation * rotationZ * rotationY * rotationX * scale. */
export function compose(pos: Vec3, rot: Vec3, scl: Vec3, out: Mat4 = mat4()): Mat4 {
  const [sx, cx] = [Math.sin(rot[0]), Math.cos(rot[0])];
  const [sy, cy] = [Math.sin(rot[1]), Math.cos(rot[1])];
  const [sz, cz] = [Math.sin(rot[2]), Math.cos(rot[2])];

  const m00 = cy * cz, m01 = cy * sz, m02 = -sy;
  const m10 = sx * sy * cz - cx * sz, m11 = sx * sy * sz + cx * cz, m12 = sx * cy;
  const m20 = cx * sy * cz + sx * sz, m21 = cx * sy * sz - sx * cz, m22 = cx * cy;

  out[0] = m00 * scl[0]; out[1] = m01 * scl[0]; out[2] = m02 * scl[0]; out[3] = 0;
  out[4] = m10 * scl[1]; out[5] = m11 * scl[1]; out[6] = m12 * scl[1]; out[7] = 0;
  out[8] = m20 * scl[2]; out[9] = m21 * scl[2]; out[10] = m22 * scl[2]; out[11] = 0;
  out[12] = pos[0]; out[13] = pos[1]; out[14] = pos[2]; out[15] = 1;
  return out;
}

/** Inverse-transpose of the upper 3x3, written as a mat3x3 padded to std140. */
export function normalMatrix(m: Mat4, out: Float32Array = new Float32Array(12)): Float32Array {
  const a = m[0]!, b = m[1]!, c = m[2]!;
  const d = m[4]!, e = m[5]!, f = m[6]!;
  const g = m[8]!, h = m[9]!, i = m[10]!;

  const A = e * i - f * h, B = f * g - d * i, C = d * h - e * g;
  const det = a * A + b * B + c * C;
  const s = Math.abs(det) > 1e-12 ? 1 / det : 0;

  // inverse-transpose == cofactor matrix / det
  out[0] = A * s;                 out[1] = B * s;                 out[2] = C * s;                 out[3] = 0;
  out[4] = (c * h - b * i) * s;   out[5] = (a * i - c * g) * s;   out[6] = (b * g - a * h) * s;   out[7] = 0;
  out[8] = (b * f - c * e) * s;   out[9] = (c * d - a * f) * s;   out[10] = (a * e - b * d) * s;  out[11] = 0;
  return out;
}

export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Frame-rate independent exponential smoothing. `rate` is roughly 1/seconds. */
export const damp = (a: number, b: number, rate: number, dt: number) =>
  lerp(a, b, 1 - Math.exp(-rate * dt));
